import { request } from '@umijs/max';

// Action Log interfaces for dashboard operations
export interface ActionLog {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  task_path: string;
  task_name: string;
  parameters: Record<string, any>;
  result: 'success' | 'failed' | 'running';
  message?: string;
  progress?: {
    processed?: number;
    total?: number;
    percentage?: number;
    success?: number;
    failed?: number;
    phase?: string;
  };
  duration?: number;
  ip_address?: string;
}

export interface ActionLogQuery {
  page?: number;
  pageSize?: number;
  user?: string;
  action?: string;
  task_path?: string;
  result?: 'success' | 'failed' | 'running';
  startDate?: string;
  endDate?: string;
}

export interface ActionLogResponse {
  success: boolean;
  data: ActionLog[];
  total: number;
  page: number;
  pageSize: number;
}

export interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  source: string;
  source_file?: string;
  line_number?: number;
  function?: string;
}

export interface LogRecord {
  pk: string;
  sk: string;
  log_type: string;
  created_at: string;
  content: {
    logs: Array<{
      timestamp: string;
      level: string;
      message: string;
    }>;
    created_at?: string;
    [key: string]: any;
  };
}

export interface LogResponse {
  logs: LogEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  available_sources: string[];
  log_summary: {
    total: number;
    by_level: Record<string, number>;
    by_source: Record<string, number>;
    recent_errors: LogEntry[];
  };
}

export interface LogStats {
  period_hours: number;
  total_entries: number;
  hourly_distribution: Record<string, { total: number; errors: number }>;
  level_distribution: Record<string, number>;
  source_distribution: Record<string, number>;
  error_rate: number;
  recent_errors: LogEntry[];
  health_status: 'healthy' | 'warning' | 'critical' | 'unknown';
}

export interface LogParams {
  page?: number;
  page_size?: number;
  level?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
  source?: string;
  log_type?: string;
  pk?: string;
}

/**
 * 获取日志列表 - 从 DynamoDB 读取
 */
export async function getLogs(params: LogParams = {}): Promise<LogResponse> {
  const response = await request<{
    success: boolean;
    data: LogRecord[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    scan_count: number;
  }>('/api/logs/lists', {
    method: 'GET',
    params: {
      page: params.page || 1,
      page_size: params.page_size || 50,
      log_type: params.log_type,
      pk: params.pk,
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date }),
    },
  });

  // 转换数据格式：将 LogRecord 转换为 LogEntry[]
  const logs: LogEntry[] = [];
  const levelCounts: Record<string, number> = {};
  const sourceCounts: Record<string, number> = {};
  const recentErrors: LogEntry[] = [];
  const sources = new Set<string>();

  response.data.forEach((record) => {
    const source = record.log_type || record.pk || 'unknown';
    sources.add(source);

    if (record.content && record.content.logs && Array.isArray(record.content.logs)) {
      record.content.logs.forEach((logItem) => {
        const level = logItem.level || 'INFO';
        const logEntry: LogEntry = {
          timestamp: logItem.timestamp || record.created_at,
          level,
          message: logItem.message || '',
          source,
        };

        // 先收集所有日志，稍后统一过滤和排序
        logs.push(logEntry);
        levelCounts[level] = (levelCounts[level] || 0) + 1;
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;

        // 收集最近的错误
        if (level === 'ERROR' || level === 'CRITICAL' || level === 'EXCEPTION') {
          recentErrors.push(logEntry);
        }
      });
    }
  });

  // 应用客户端过滤
  let filteredLogs = logs;
  if (params.level) {
    filteredLogs = filteredLogs.filter(log => log.level === params.level);
  }
  if (params.search) {
    const searchLower = params.search.toLowerCase();
    filteredLogs = filteredLogs.filter(log => 
      log.message.toLowerCase().includes(searchLower) ||
      log.source.toLowerCase().includes(searchLower)
    );
  }
  if (params.source) {
    filteredLogs = filteredLogs.filter(log => log.source === params.source);
  }

  // 按时间戳排序（最新的在前）
  filteredLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // 计算错误率（基于过滤后的日志）
  const filteredLevelCounts: Record<string, number> = {};
  filteredLogs.forEach(log => {
    filteredLevelCounts[log.level] = (filteredLevelCounts[log.level] || 0) + 1;
  });
  const totalErrors = (filteredLevelCounts['ERROR'] || 0) + (filteredLevelCounts['CRITICAL'] || 0) + (filteredLevelCounts['EXCEPTION'] || 0);
  const errorRate = filteredLogs.length > 0 ? (totalErrors / filteredLogs.length) * 100 : 0;

  return {
    logs: filteredLogs,
    total: filteredLogs.length, // 使用过滤后的总数
    page: response.page,
    page_size: response.page_size,
    total_pages: Math.ceil(filteredLogs.length / response.page_size),
    available_sources: Array.from(sources),
    log_summary: {
      total: filteredLogs.length,
      by_level: filteredLevelCounts,
      by_source: sourceCounts,
      recent_errors: recentErrors.slice(0, 10),
    },
  };
}

/**
 * 获取日志统计信息 - 简化版本
 */
export async function getLogStats(hours: number = 24): Promise<LogStats> {
  // 获取日志数据来计算统计信息
  // 限制 page_size 为 200，避免 DynamoDB 吞吐量超限
  const logResponse = await getLogs({ page_size: 200 });
  
  const levelCounts: Record<string, number> = {};
  const sourceCounts: Record<string, number> = {};
  const recentErrors: LogEntry[] = [];
  const hourlyDistribution: Record<string, { total: number; errors: number }> = {};

  logResponse.logs.forEach((log) => {
    const level = log.level;
    const source = log.source;
    const timestamp = new Date(log.timestamp);
    const hour = timestamp.toISOString().substring(0, 13) + ':00:00';

    levelCounts[level] = (levelCounts[level] || 0) + 1;
    sourceCounts[source] = (sourceCounts[source] || 0) + 1;

    if (!hourlyDistribution[hour]) {
      hourlyDistribution[hour] = { total: 0, errors: 0 };
    }
    hourlyDistribution[hour].total++;
    if (level === 'ERROR' || level === 'CRITICAL') {
      hourlyDistribution[hour].errors++;
      recentErrors.push(log);
    }
  });

  const totalErrors = (levelCounts['ERROR'] || 0) + (levelCounts['CRITICAL'] || 0);
  const errorRate = logResponse.logs.length > 0 ? (totalErrors / logResponse.logs.length) * 100 : 0;

  let healthStatus: 'healthy' | 'warning' | 'critical' | 'unknown' = 'unknown';
  if (errorRate > 10) {
    healthStatus = 'critical';
  } else if (errorRate > 5) {
    healthStatus = 'warning';
  } else if (errorRate >= 0) {
    healthStatus = 'healthy';
  }

  return {
    period_hours: hours,
    total_entries: logResponse.logs.length,
    hourly_distribution: hourlyDistribution,
    level_distribution: levelCounts,
    source_distribution: sourceCounts,
    error_rate: errorRate,
    recent_errors: recentErrors.slice(0, 10),
    health_status: healthStatus,
  };
}

/**
 * 导出日志
 * 注意：由于 DynamoDB 吞吐量限制，导出功能会分页获取数据
 */
export async function exportLogs(params: LogParams = {}): Promise<Blob> {
  // 限制单次请求的 page_size，避免 DynamoDB 吞吐量超限
  // 如果需要导出更多数据，可以多次请求并合并
  const maxPageSize = 200;
  const logResponse = await getLogs({ ...params, page_size: maxPageSize });
  
  // 将日志转换为 CSV 格式
  const csvHeader = 'Timestamp,Level,Source,Message\n';
  const csvRows = logResponse.logs.map(log => 
    `"${log.timestamp}","${log.level}","${log.source}","${log.message.replace(/"/g, '""')}"`
  ).join('\n');
  
  const csvContent = csvHeader + csvRows;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // 触发下载
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `logs_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  return blob;
}

/**
 * 清理旧日志 - 暂不支持
 */
export async function cleanOldLogs(days: number = 30): Promise<{ success: boolean; message: string }> {
  return Promise.resolve({
    success: false,
    message: '清理功能暂未实现',
  });
}

/**
 * 测试日志记录 - 暂不支持
 */
export async function testLog(level: string, message: string): Promise<{ success: boolean; message: string }> {
  return Promise.resolve({
    success: false,
    message: '测试日志功能暂未实现',
  });
}

// Action Logging Functions
/**
 * 记录用户操作日志
 */
export async function logAction(log: Omit<ActionLog, 'id' | 'timestamp'>): Promise<void> {
  try {
    const actionLogData = {
      ...log,
      timestamp: new Date().toISOString(),
      id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
    
    // 发送到后端API
    await request<void>('/api/logs/action', {
      method: 'POST',
      data: actionLogData,
    });
    
    console.log('Action logged successfully:', actionLogData);
  } catch (error) {
    console.error('Failed to log action:', error);
    // 不阻止正常流程，即使日志记录失败也继续
  }
}

/**
 * 查询操作日志
 */
export async function getActionLogs(query: ActionLogQuery = {}): Promise<ActionLogResponse> {
  try {
    return await request<ActionLogResponse>('/api/logs/action', {
      method: 'GET',
      params: {
        page: 1,
        pageSize: 20,
        ...query,
      },
    });
  } catch (error) {
    console.error('Failed to fetch action logs from API, using fallback:', error);
    // 如果API失败，返回空数据结构
    return {
      success: true,
      data: [],
      total: 0,
      page: query.page || 1,
      pageSize: query.pageSize || 20,
    };
  }
}

/**
 * 获取用户操作统计
 */
export async function getUserActionStats(): Promise<{
  success: boolean;
  data: {
    total_actions: number;
    successful_actions: number;
    failed_actions: number;
    today_actions: number;
    recent_actions: ActionLog[];
  };
}> {
  return request<any>('/api/logs/action/stats', {
    method: 'GET',
  });
}

/**
 * 导出操作日志（按当前筛选条件分页拉取全部，再导出为 CSV）
 * 后端单页上限 100，因此分页请求直至取完所有符合条件的数据
 */
export async function exportActionLogs(query: ActionLogQuery = {}): Promise<void> {
  try {
    const pageSize = 100; // 与后端 /api/logs/action 的 pageSize 上限一致
    const { page: _p, pageSize: _ps, ...rest } = query;
    let allData: ActionLog[] = [];
    let page = 1;
    let total = 0;

    do {
      const response = await getActionLogs({
        ...rest,
        page,
        pageSize,
      });
      if (!response.success) break;
      const list = response.data ?? [];
      total = response.total ?? 0;
      allData = allData.concat(list);
      if (list.length < pageSize || allData.length >= total) break;
      page += 1;
    } while (true);

    const csvHeader = 'Timestamp,User,Action,Task Path,Task Name,Parameters,Result,Message,Duration,IP Address\n';
    const csvRows = allData.map(log =>
      `"${log.timestamp}","${log.user}","${log.action}","${log.task_path}","${log.task_name}","${JSON.stringify(log.parameters ?? {}).replace(/"/g, '""')}","${log.result}","${(log.message ?? '').replace(/"/g, '""')}","${log.duration ?? ''}","${log.ip_address ?? ''}"`
    ).join('\n');

    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `action_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export action logs:', error);
    throw error;
  }
}